import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { AISetupModal } from "../../components/ai/AISetupModal";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import { Header } from "../../components/layout/Header";
import { ProjectCard } from "../../components/projects/ProjectCard";
import { SessionSearch } from "../../components/stream/SessionSearch";
import { useAuth } from "../../hooks/useAuth";
import { projectAPI, APIError } from "../../lib/api";
import { useAIConfig } from "../../contexts/AIConfigContext";
import type { ProjectResponse } from "../../types/api";
import styles from "./ProjectDashboard.module.css";

const PROJECTS_PER_PAGE = 20;

type QuickStartMode = "stream" | "outline" | "draft";

export default function ProjectDashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const organizations = user?.organizations ?? [];
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const quickStartRef = useRef<HTMLDivElement>(null);

  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [projects, setProjects] = useState<ProjectResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [errorSource, setErrorSource] = useState<"load" | "quickstart">("load");
  const [lastAttemptedMode, setLastAttemptedMode] = useState<QuickStartMode>("stream");
  const [titleConflict, setTitleConflict] = useState<string | null>(null);

  // Quick start state
  const [quickStartTitle, setQuickStartTitle] = useState("");
  const [quickStartText, setQuickStartText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  // AI configuration status (shared context, re-checks on navigation)
  const { aiConfigured, aiEnabled, refreshAIStatus } = useAIConfig();

  // AI setup modal (shown when user tries to start an AI-dependent mode without config)
  const [showAISetup, setShowAISetup] = useState(false);

  // Dismissible AI banner
  const [aiBannerDismissed, setAiBannerDismissed] = useState(() => {
    try {
      return localStorage.getItem("hemingway_ai_banner_dismissed") === "true";
    } catch {
      return false;
    }
  });

  const dismissAiBanner = () => {
    setAiBannerDismissed(true);
    try {
      localStorage.setItem("hemingway_ai_banner_dismissed", "true");
    } catch {
      // ignore
    }
  };

  // Pagination state
  const [visibleCount, setVisibleCount] = useState(PROJECTS_PER_PAGE);

  // Search / filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [modeFilter, setModeFilter] = useState<"all" | "stream" | "outline" | "edit">("all");

  // Inline duplicate title detection — matches against loaded projects
  const duplicateProject = useMemo(() => {
    const trimmed = quickStartTitle.trim().toLowerCase();
    if (!trimmed) return null;
    return projects.find((p) => p.title.toLowerCase() === trimmed) ?? null;
  }, [quickStartTitle, projects]);

  // Pending navigation target — set after project creation so that navigation
  // happens in a dedicated effect, after React has flushed all state updates.
  // Fixes #1513 where calling navigate() inside an async handler could be
  // swallowed by a concurrent state-driven re-render.
  const [pendingNavigate, setPendingNavigate] = useState<string | null>(null);

  useEffect(() => {
    if (pendingNavigate) {
      navigate(pendingNavigate);
      setPendingNavigate(null);
    }
  }, [pendingNavigate, navigate]);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (organizations.length > 0 && !selectedOrgId) {
      setSelectedOrgId(organizations[0].id);
    }
  }, [organizations, selectedOrgId]);

  const retryCountRef = useRef(0);

  const loadProjects = useCallback(async () => {
    if (!selectedOrgId) return;

    setLoading(true);
    setError(null);
    setIsNetworkError(false);
    setTitleConflict(null);

    try {
      const data = await projectAPI.getAll(selectedOrgId);
      setProjects(data);
      retryCountRef.current = 0;
    } catch (err) {
      const isServer = err instanceof APIError && err.statusCode >= 500;
      const isNetwork = err instanceof APIError && err.statusCode === 0;

      // Auto-retry once on server errors (handles post-login race condition)
      if (isServer && retryCountRef.current < 1) {
        retryCountRef.current += 1;
        setTimeout(() => {
          loadProjects();
        }, 1500);
        return;
      }

      setErrorSource("load");
      if (isNetwork) {
        setIsNetworkError(true);
        setError(
          t(
            "quickStart.networkError",
            "Unable to connect to server. Please check your connection.",
          ),
        );
      } else if (isServer) {
        setIsNetworkError(true); // show retry button
        setError(
          t(
            "dashboard.serverError",
            "Something went wrong loading your projects. Please try again.",
          ),
        );
      } else {
        setError(err instanceof Error ? err.message : "Failed to load projects");
      }
    } finally {
      setLoading(false);
    }
  }, [selectedOrgId, t]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [quickStartText]);

  const handleQuickStart = async (mode: QuickStartMode = "stream") => {
    // Read from the DOM ref as the source of truth to guard against React
    // controlled-input edge cases where state can momentarily drift from the
    // actual textarea value (e.g. after an error re-render cycle).
    const text = (textareaRef.current?.value ?? quickStartText).trim();
    const title =
      quickStartTitle.trim() ||
      (text
        ? generateUniqueTitle(deriveTitleFromText(text))
        : generateUniqueTitle(generateDefaultTitle(mode)));
    if (!selectedOrgId || isSubmittingRef.current) return;
    setLastAttemptedMode(mode);

    // Synchronous ref guard prevents duplicate submissions from rapid clicks
    // (state updates are async and can't reliably gate concurrent calls).
    isSubmittingRef.current = true;

    // AI is optional for all modes including Outline.
    // Users can always create manual outlines; AI just enhances the experience.

    // Sync React state with the DOM value so the controlled input stays in
    // sync after we read from the ref above.
    if (textareaRef.current && textareaRef.current.value !== quickStartText) {
      setQuickStartText(textareaRef.current.value);
    }

    setIsSubmitting(true);
    setError(null);
    setIsNetworkError(false);
    setTitleConflict(null);

    try {
      // Create project with auto-generated title
      // Map quick-start mode to project mode ("draft" → "edit" in the backend)
      const modeMap: Record<QuickStartMode, "stream" | "outline" | "edit"> = {
        stream: "stream",
        outline: "outline",
        draft: "edit",
      };

      const newProject = await projectAPI.create(selectedOrgId, {
        title,
        description: text || null,
        currentMode: modeMap[mode],
        // Seed the thought session server-side so the Stream view always
        // contains the original thought, regardless of starting mode.
        // Fixes #945 (previously only sent for stream mode per #921).
        ...(text ? { initialThought: text } : {}),
      });

      // Store initial content in sessionStorage for ProjectDetail to verify.
      // Stream mode seeds the thought session server-side, but we still
      // store here so ProjectDetail can confirm the content was persisted
      // and re-send it if the server-side seeding failed. Fixes #1547.
      if (text) {
        sessionStorage.setItem(`project_${newProject.id}_initial_thought`, text);
        sessionStorage.setItem(`project_${newProject.id}_initial_mode`, mode);
      }

      // Mark as newly created so ProjectDetail can auto-prune if the user
      // navigates away without adding content. Fixes #1608.
      if (!text?.trim()) {
        sessionStorage.setItem(`project_${newProject.id}_is_empty_new`, "1");
      }

      toast.success(t("dashboard.projectCreated", { title: newProject.title }));
      setPendingNavigate(`/organizations/${selectedOrgId}/projects/${newProject.id}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to start writing";
      if (message.toLowerCase().includes("already exists")) {
        setTitleConflict(title || generateDefaultTitle(mode));
      } else {
        const networkFailure =
          message.toLowerCase().includes("network error") ||
          message.toLowerCase().includes("unable to connect") ||
          message.toLowerCase().includes("failed to fetch");
        setIsNetworkError(networkFailure);
        setErrorSource("quickstart");
        const displayMessage = networkFailure
          ? t(
              "quickStart.networkError",
              "Could not save your stream. Check your connection and try again.",
            )
          : message;
        setError(displayMessage);
        toast.error(displayMessage, { id: "quickstart-error" });
      }
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  // Keep a stable ref to the latest handleQuickStart so the keydown callback
  // never closes over a stale version.
  const handleQuickStartRef = useRef(handleQuickStart);
  handleQuickStartRef.current = handleQuickStart;

  // Handle Cmd/Ctrl+Enter to submit from anywhere in the quick-start form
  // (title input *and* textarea). Fixes #808.
  const handleQuickStartKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      e.stopPropagation();
      handleQuickStartRef.current();
    }
  }, []);

  /** Derive a project title from the user's stream text (first line, max 200 chars).
   *  Uses a generous limit to avoid data loss — display truncation is handled by CSS. */
  const deriveTitleFromText = useCallback((text: string): string => {
    const firstLine = text.split(/[\n\r]/)[0].trim();
    if (firstLine.length <= 200) return firstLine;
    // Truncate at word boundary for very long single-line inputs
    const truncated = firstLine.slice(0, 200).replace(/\s+\S*$/, "");
    return truncated || firstLine.slice(0, 200);
  }, []);

  // Global Ctrl/Cmd+Enter listener as a fallback so the shortcut works even
  // when the capture-phase global keyboard-shortcuts handler consumes the
  // event before React's synthetic onKeyDown fires.  Fixes #1531.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        // Only trigger when focus is inside the quick-start card (or nowhere
        // special) so we don't hijack Ctrl+Enter in other contexts.
        const active = document.activeElement;
        const inQuickStart =
          !active || active === document.body || quickStartRef.current?.contains(active);
        if (inQuickStart) {
          e.preventDefault();
          e.stopImmediatePropagation();
          handleQuickStartRef.current();
        }
      }
    };
    // Use capture phase to fire before the global shortcuts handler.
    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, []);

  const generateDefaultTitle = useCallback(
    (mode: QuickStartMode): string => {
      const modeLabels: Record<QuickStartMode, string> = {
        stream: t("modes.stream"),
        outline: t("modes.outline"),
        draft: t("modes.draft"),
      };
      const date = new Date().toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
      return `${modeLabels[mode]} – ${date}`;
    },
    [t],
  );

  const generateUniqueTitle = useCallback(
    (base: string): string => {
      const existingTitles = new Set(projects.map((p) => p.title));
      if (!existingTitles.has(base)) return base;
      let counter = 2;
      while (existingTitles.has(`${base} (${counter})`)) counter++;
      return `${base} (${counter})`;
    },
    [projects],
  );

  const handleDeleteProject = (projectId: string) => {
    const project = projects.find((p) => p.id === projectId);
    setDeleteTarget({ id: projectId, title: project?.title ?? "this project" });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    try {
      await projectAPI.delete(selectedOrgId, deleteTarget.id);
      setProjects((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      toast.success("Project deleted");
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete project");
    } finally {
      setIsDeleting(false);
    }
  };

  const selectedOrg = organizations.find((o) => o.id === selectedOrgId);
  const hasOrg = !!selectedOrgId;

  const filteredProjects = projects.filter((p) => {
    // Mode filter
    if (modeFilter !== "all" && p.current_mode !== modeFilter) return false;
    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      return (
        p.title.toLowerCase().includes(q) ||
        p.current_mode.toLowerCase().includes(q) ||
        (p.description && p.description.toLowerCase().includes(q)) ||
        (p.content_preview && p.content_preview.toLowerCase().includes(q))
      );
    }
    return true;
  });

  const paginatedProjects = filteredProjects.slice(0, visibleCount);
  const hasMore = filteredProjects.length > visibleCount;

  return (
    <div className={styles.page}>
      <Header />
      <main className={styles.container}>
        <header className={styles.pageHeader}>
          <div className={styles.pageTitleRow}>
            <h1 className={styles.pageTitle}>{t("dashboard.yourWriting")}</h1>
            {aiEnabled && aiConfigured === false && aiBannerDismissed && (
              <button
                type="button"
                className={styles.setupAIHint}
                onClick={() => setShowAISetup(true)}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.663 17h4.674M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l.146.146A3.004 3.004 0 0117 19.5V20a2 2 0 01-2 2h-6a2 2 0 01-2-2v-.5c0-.796-.316-1.559-.878-2.121l.146-.146z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {t("quickStart.setupAI")}
              </button>
            )}
          </div>
          {selectedOrg && organizations.length > 1 && (
            <div className={styles.orgSelector}>
              <select
                id="org-select"
                className={styles.orgSelect}
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
              >
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </header>

        {/* AI Setup Banner — shown above the creation card so it doesn't push inputs down */}
        {aiEnabled && aiConfigured !== true && !aiBannerDismissed && (
          <div className={styles.aiBanner}>
            <div className={styles.aiBannerContent}>
              <svg
                className={styles.aiBannerIcon}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 5.33333V8M8 10.6667H8.00667M14.6667 8C14.6667 11.6819 11.6819 14.6667 8 14.6667C4.3181 14.6667 1.33333 11.6819 1.33333 8C1.33333 4.3181 4.3181 1.33333 8 1.33333C11.6819 1.33333 14.6667 4.3181 14.6667 8Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{t("quickStart.connectAIProvider")}</span>
            </div>
            <div className={styles.aiBannerActions}>
              <button
                type="button"
                className={styles.aiBannerAction}
                onClick={() => setShowAISetup(true)}
              >
                {t("quickStart.setupAI")}
              </button>
              <button
                type="button"
                className={styles.aiBannerDismiss}
                onClick={dismissAiBanner}
                aria-label={t("common.dismiss")}
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        )}

        {/* Quick Start Input */}
        <div className={styles.quickStart} ref={quickStartRef}>
          <div className={styles.quickStartCard}>
            <input
              type="text"
              className={`${styles.quickStartTitleInput} ${duplicateProject ? styles.quickStartTitleInputConflict : ""}`}
              value={quickStartTitle}
              onChange={(e) => setQuickStartTitle(e.target.value)}
              onKeyDown={handleQuickStartKeyDown}
              placeholder={t("quickStart.titlePlaceholder")}
              disabled={!hasOrg || isSubmitting}
              maxLength={255}
            />

            {duplicateProject && (
              <div className={styles.inlineTitleConflict}>
                <span>{t("dashboard.inlineTitleConflict", { title: quickStartTitle.trim() })}</span>
                <button
                  type="button"
                  className={styles.inlineTitleConflictLink}
                  onClick={() => {
                    setPendingNavigate(
                      `/organizations/${selectedOrgId}/projects/${duplicateProject.id}`,
                    );
                  }}
                >
                  {t("dashboard.openExisting")}
                </button>
              </div>
            )}

            <div className={styles.inputWrapper}>
              <textarea
                ref={textareaRef}
                className={styles.quickStartInput}
                value={quickStartText}
                onChange={(e) => {
                  setQuickStartText(e.target.value);
                  if (error || titleConflict) {
                    setError(null);
                    setIsNetworkError(false);
                    setTitleConflict(null);
                  }
                }}
                onKeyDown={handleQuickStartKeyDown}
                placeholder={t("quickStart.streamPlaceholder")}
                rows={3}
                disabled={!hasOrg || isSubmitting}
              />
            </div>

            <div className={styles.startButtonGroup}>
              <button
                type="button"
                className={`${styles.startButton} ${styles.startButtonStream}`}
                onClick={() => handleQuickStart("stream")}
                disabled={!hasOrg || isSubmitting}
                aria-label={t("quickStart.goStream")}
                title={
                  !hasOrg
                    ? t("dashboard.createOrgToStart")
                    : `${t("modes.stream")} (${/(Mac|iPhone|iPod|iPad)/i.test(navigator.platform) ? "⌘" : "Ctrl"}+Enter)`
                }
              >
                {isSubmitting ? (
                  <SpinnerIcon />
                ) : (
                  <>
                    <StreamIcon />
                    <span className={styles.startButtonLabel}>{t("modes.stream")}</span>
                  </>
                )}
              </button>
              <button
                type="button"
                className={`${styles.startButton} ${styles.startButtonOutline}`}
                onClick={() => handleQuickStart("outline")}
                disabled={!hasOrg || isSubmitting}
                aria-label={t("quickStart.goOutline")}
                title={!hasOrg ? t("dashboard.createOrgToStart") : t("quickStart.goOutline")}
              >
                {isSubmitting ? (
                  <SpinnerIcon />
                ) : (
                  <>
                    <OutlineIcon />
                    <span className={styles.startButtonLabel}>{t("modes.outline")}</span>
                  </>
                )}
              </button>
              <button
                type="button"
                className={`${styles.startButton} ${styles.startButtonDraft}`}
                onClick={() => handleQuickStart("draft")}
                disabled={!hasOrg || isSubmitting}
                aria-label={t("quickStart.goDraft")}
                title={!hasOrg ? t("dashboard.createOrgToStart") : t("quickStart.goDraft")}
              >
                {isSubmitting ? (
                  <SpinnerIcon />
                ) : (
                  <>
                    <DraftIcon />
                    <span className={styles.startButtonLabel}>{t("modes.draft")}</span>
                  </>
                )}
              </button>
            </div>

            {!hasOrg && <p className={styles.quickStartHint}>{t("dashboard.createOrgToStart")}</p>}
          </div>
        </div>

        {error && (
          <div className={styles.error}>
            <span>{error}</span>
            {isNetworkError && (
              <button
                type="button"
                className={styles.retryButton}
                onClick={() => {
                  setError(null);
                  setIsNetworkError(false);
                  if (errorSource === "load") {
                    retryCountRef.current = 0;
                    loadProjects();
                  } else {
                    handleQuickStartRef.current(lastAttemptedMode);
                  }
                }}
              >
                {t("quickStart.retry", "Retry")}
              </button>
            )}
          </div>
        )}

        {titleConflict && (
          <div className={styles.titleConflict}>
            <p className={styles.titleConflictMessage}>
              {t("dashboard.titleConflict", { title: titleConflict })}
            </p>
            <div className={styles.titleConflictActions}>
              <button
                type="button"
                className={styles.titleConflictButton}
                onClick={() => {
                  const existing = projects.find(
                    (p) => p.title.toLowerCase() === titleConflict.toLowerCase(),
                  );
                  if (existing && selectedOrgId) {
                    setTitleConflict(null);
                    setPendingNavigate(`/organizations/${selectedOrgId}/projects/${existing.id}`);
                  }
                }}
              >
                {t("dashboard.openExisting")}
              </button>
              <button
                type="button"
                className={styles.titleConflictButtonSecondary}
                onClick={async () => {
                  if (!selectedOrgId) return;
                  setTitleConflict(null);
                  const copyTitle = generateUniqueTitle(titleConflict);
                  setQuickStartTitle(copyTitle);
                  // Re-trigger with the new unique title
                  setIsSubmitting(true);
                  isSubmittingRef.current = true;
                  try {
                    const text = quickStartText.trim();
                    const modeMap: Record<QuickStartMode, "stream" | "outline" | "edit"> = {
                      stream: "stream",
                      outline: "outline",
                      draft: "edit",
                    };
                    const defaultMode: QuickStartMode = "stream";
                    const newProject = await projectAPI.create(selectedOrgId, {
                      title: copyTitle,
                      description: text || null,
                      currentMode: modeMap[defaultMode],
                      ...(text ? { initialThought: text } : {}),
                    });
                    if (text) {
                      sessionStorage.setItem(`project_${newProject.id}_initial_thought`, text);
                      sessionStorage.setItem(`project_${newProject.id}_initial_mode`, defaultMode);
                    }
                    setPendingNavigate(`/organizations/${selectedOrgId}/projects/${newProject.id}`);
                  } catch (err) {
                    const message = err instanceof Error ? err.message : "Failed to create project";
                    setError(message);
                  } finally {
                    setIsSubmitting(false);
                    isSubmittingRef.current = false;
                  }
                }}
              >
                {t("dashboard.createCopy")}
              </button>
              <button
                type="button"
                className={styles.titleConflictDismiss}
                onClick={() => setTitleConflict(null)}
                aria-label={t("common.dismiss")}
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Projects Section */}
        {hasOrg && (
          <section className={styles.projectsSection}>
            <div className={styles.projectsSectionHeader}>
              <div className={styles.sectionTitleGroup}>
                <h2 className={styles.sectionTitle}>{t("projects.title")}</h2>
                <button
                  type="button"
                  className={styles.newProjectButton}
                  onClick={() => {
                    quickStartRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    // Focus the title input after scroll
                    setTimeout(() => {
                      const titleInput = quickStartRef.current?.querySelector("input");
                      titleInput?.focus();
                    }, 400);
                  }}
                  aria-label={t("dashboard.newProject")}
                >
                  <PlusIcon />
                  <span>{t("dashboard.newProject")}</span>
                </button>
              </div>
              <SessionSearch
                orgId={selectedOrgId}
                onQueryChange={(q) => {
                  setSearchQuery(q);
                  setVisibleCount(PROJECTS_PER_PAGE);
                }}
                placeholder={t("projects.searchPlaceholder")}
                ariaLabel={t("projects.searchAriaLabel")}
                emptyNoun={t("projects.noun")}
              />
            </div>

            {projects.length > 0 && (
              <div
                className={styles.filterChips}
                role="group"
                aria-label={t("projects.filterByMode")}
              >
                {(["all", "stream", "outline", "edit"] as const).map((mode) => {
                  const count =
                    mode === "all"
                      ? projects.length
                      : projects.filter((p) => p.current_mode === mode).length;
                  const labels: Record<string, string> = {
                    all: t("filters.all"),
                    stream: t("modes.stream"),
                    outline: t("modes.outline"),
                    edit: t("modes.draft"),
                  };
                  return (
                    <button
                      key={mode}
                      type="button"
                      className={`${styles.filterChip} ${modeFilter === mode ? styles.filterChipActive : ""}`}
                      onClick={() => {
                        setModeFilter(mode);
                        setVisibleCount(PROJECTS_PER_PAGE);
                      }}
                      aria-pressed={modeFilter === mode}
                    >
                      {labels[mode]}
                      <span className={styles.filterChipCount}>{count}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {loading ? (
              <div className={styles.loadingGrid}>
                {[1, 2, 3].map((i) => (
                  <div key={i} className={styles.skeletonCard}>
                    <div className={styles.skeletonTitle} />
                    <div className={styles.skeletonMeta} />
                  </div>
                ))}
              </div>
            ) : filteredProjects.length > 0 ? (
              <>
                <div className={styles.projectGrid}>
                  {paginatedProjects.map((project) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      orgId={selectedOrgId}
                      onDelete={handleDeleteProject}
                      onRename={(id, newTitle) =>
                        setProjects((prev) =>
                          prev.map((p) => (p.id === id ? { ...p, title: newTitle } : p)),
                        )
                      }
                    />
                  ))}
                </div>
                {hasMore && (
                  <div className={styles.loadMoreWrapper}>
                    <button
                      type="button"
                      className={styles.loadMoreButton}
                      onClick={() => setVisibleCount((v) => v + PROJECTS_PER_PAGE)}
                    >
                      {t("projects.loadMore", {
                        remaining: filteredProjects.length - visibleCount,
                      })}
                    </button>
                    <span className={styles.projectCount}>
                      {t("projects.showingCount", {
                        shown: paginatedProjects.length,
                        total: filteredProjects.length,
                      })}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <p className={styles.emptyProjects}>
                {searchQuery.trim()
                  ? t("dashboard.noProjectsMatching", { query: searchQuery.trim() })
                  : t("dashboard.noProjectsYet")}
              </p>
            )}
          </section>
        )}
      </main>

      {showAISetup && (
        <AISetupModal
          onClose={() => setShowAISetup(false)}
          onConfigured={() => {
            setShowAISetup(false);
            refreshAIStatus();
          }}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete Project"
          message={`Are you sure you want to delete "${deleteTarget.title}"? This cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          variant="danger"
          isLoading={isDeleting}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function StreamIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8 2V14M4 5L8 2L12 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function OutlineIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5 4H14M5 8H14M5 12H11M2 4H2.01M2 8H2.01M2 12H2.01"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DraftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9 2H4C3.44772 2 3 2.44772 3 3V13C3 13.5523 3.44772 14 4 14H12C12.5523 14 13 13.5523 13 13V6L9 2Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 2V6H13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7 1V13M1 7H13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={styles.spinner}
    >
      <circle
        cx="10"
        cy="10"
        r="8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="50"
        strokeDashoffset="35"
      />
    </svg>
  );
}
